<template>
    <button
        v-if="!showInlineTitle"
        v-tooltip:[tooltipArg]="tooltipOptions"
        type="button"
        class="p-button common-action-button"
        :class="buttonClasses"
        :aria-label="ariaLabel"
        @click.stop="handleClick"
    >
        <slot />
        <font-awesome-icon
            v-if="icon"
            :icon="icon"
            :style="iconStyle"
        />
        <i
            v-else-if="iconClassString"
            :class="iconClassString"
            :style="iconStyle"
        />
    </button>
    <button
        v-else
        type="button"
        class="p-button common-action-button"
        :class="buttonClasses"
        :aria-label="ariaLabel"
        @click.stop="handleClick"
    >
        <slot />
        <font-awesome-icon
            v-if="icon"
            :icon="icon"
            :style="iconStyle"
        />
        <i
            v-else-if="iconClassString"
            :class="iconClassString"
            :style="iconStyle"
        />
        <span v-if="props.showTitleOnMobileView" class="inline-title">{{ props.title }}</span>
    </button>
</template>

<script setup lang="ts">
import { computed, StyleValue } from 'vue';
import { storeToRefs } from 'pinia';
import { useMediaQuery } from '@vueuse/core';
import { useUIStore } from '@/stores/uiStore.ts';
import { getTooltipDirectiveArg, Position } from '@/types/general/Direction.ts';

const DEFAULT_TOOLTIP_POSITION: Position = 'top';

const uiStore = useUIStore();
const { isMobileView } = storeToRefs(uiStore);
const hasHoverPointer = useMediaQuery('(hover: hover) and (pointer: fine)');

const props = defineProps<{
    title: string;
    nonTextButton?: boolean;
    icon?: [string, string];
    iconClassString?: string;
    isDisabled?: boolean;
    disabledReason?: string;
    color?: string;
    direction?: Position;
    showTitleOnMobileView?: boolean;
}>();

const emit = defineEmits<{(event: 'click', e: MouseEvent): void;
}>();

const ariaLabel = computed(() => {
    if (!props.isDisabled) return props.title;

    return `${props.title} - DISABLED${
        props.disabledReason ? ` - ${props.disabledReason}` : ''
    }`;
});

const displayText = computed<string>(() => (props.isDisabled && props.disabledReason ? props.disabledReason : props.title));

const tooltipText = computed<string | null>(() => (isMobileView.value ? null : displayText.value));

const showInlineTitle = computed<boolean>(() => props.showTitleOnMobileView && isMobileView.value);

const buttonClasses = computed<Record<string, boolean>>(() => ({
    disabled: props.isDisabled,
    'p-button-text': !props.nonTextButton,
    'list-mode': showInlineTitle.value,
}));

const iconStyle = computed<StyleValue | undefined>(() => (props.color ? { color: props.color } : undefined));

const tooltipArg = computed(() => getTooltipDirectiveArg(
    props.direction ?? DEFAULT_TOOLTIP_POSITION,
    !hasHoverPointer.value,
));

const tooltipOptions = computed(() => ({
    value: tooltipText.value,
}));

function handleClick (e: MouseEvent): void {
    if (!props.isDisabled) {
        emit('click', e);
    }
}
</script>

<style scoped lang="scss">

.common-action-button {
    background: transparent;
    border: none;
    font-size: var(--defaultFontSize);
    overflow: hidden;
    position: relative;

    &.delete {
        color: var(--dangerRed);
    }

    &.disabled {
        cursor: default;
        opacity: 0.5;
    }

    &.list-mode {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: var(--defaultSpacing);
        width: 100%;
        padding: var(--smallSpacing) var(--defaultSpacing);
    }
}

.inline-title {
    white-space: nowrap;
}

</style>
